import React from 'react';
import { type TimelineEvent, getActionLabel, getActionColorCategory } from '@/hooks/useTimelineDecisoes';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  User,
  Mail,
  Clock,
  FileText,
  ArrowRightLeft,
  AlertTriangle,
  Zap,
  Shield,
  Hash,
  Globe,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TIPO_LABELS: Record<string, string> = {
  transicao: 'Transição de Workflow',
  aprovacao: 'Aprovação / Rejeição',
  auditoria: 'Log de Auditoria',
};

const COLOR_MAP: Record<string, string> = {
  creation: 'text-blue-400',
  alteration: 'text-amber-400',
  approval: 'text-emerald-400',
  rejection: 'text-red-400',
  deletion: 'text-muted-foreground',
  comment: 'text-purple-400',
};

function formatFull(d: string) {
  try {
    return format(new Date(d), "EEEE, dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss", { locale: ptBR });
  } catch {
    return d;
  }
}

interface Props {
  event: TimelineEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</p>
      {children}
    </div>
  );
}

function DiffView({ label, data }: { label: string; data: Record<string, unknown> }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>
      <pre className="text-xs bg-muted/50 rounded-lg p-3 overflow-x-auto max-h-48 border border-border/30">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

export const TimelineDetailSheet: React.FC<Props> = ({ event, open, onOpenChange }) => {
  if (!event) return null;

  const colorCat = getActionColorCategory(event.acao);
  const colorClass = COLOR_MAP[colorCat] || COLOR_MAP.alteration;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-hidden flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">{TIPO_LABELS[event.tipo] || event.tipo}</Badge>
            {event.is_critical && (
              <Badge variant="destructive" className="text-xs">
                <AlertTriangle className="w-3 h-3 mr-0.5" /> Crítico
              </Badge>
            )}
          </div>
          <SheetTitle className={`text-base ${colorClass}`}>
            {getActionLabel(event.acao)}
          </SheetTitle>
          <SheetDescription className="text-sm">
            {event.descricao}
          </SheetDescription>
        </SheetHeader>

        <Separator className="my-3" />

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-5 pb-6">
            {/* User info */}
            <Section title="Usuário">
              <div className="space-y-1.5">
                {event.usuario_nome ? (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{event.usuario_nome}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span className="italic">Usuário não registrado</span>
                  </div>
                )}
                {event.usuario_email ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <span>{event.usuario_email}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <span className="italic">Email não disponível</span>
                  </div>
                )}
                {event.usuario_role && (
                  <Badge variant="secondary" className="text-xs">{event.usuario_role}</Badge>
                )}
              </div>
            </Section>

            {/* Timestamp */}
            <Section title="Data e Hora">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>{formatFull(event.created_at)}</span>
              </div>
            </Section>

            {/* Contract */}
            {event.contrato_numero && (
              <Section title="Contrato">
                <div className="flex items-center gap-2 text-sm">
                  <Hash className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{event.contrato_numero}</span>
                  {event.contrato_nome && (
                    <span className="text-muted-foreground">— {event.contrato_nome}</span>
                  )}
                </div>
              </Section>
            )}

            {/* Action type */}
            <Section title="Tipo de Ação">
              <Badge variant="outline" className="text-xs">
                {getActionLabel(event.acao)}
              </Badge>
            </Section>

            {/* Document reference */}
            {event.documento_ref && (
              <Section title="Documento / Formulário">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="font-mono text-xs">{event.documento_ref}</span>
                </div>
              </Section>
            )}

            {/* Status change */}
            {event.status_anterior && event.status_novo && (
              <Section title="Mudança de Status">
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">{event.status_anterior}</Badge>
                  <ArrowRightLeft className="w-4 h-4 text-muted-foreground" />
                  <Badge variant="outline">{event.status_novo}</Badge>
                </div>
              </Section>
            )}

            {/* Output generated */}
            {event.output_gerado && (
              <Section title="Output Gerado">
                <div className="flex items-start gap-2 text-sm bg-muted/30 rounded-lg p-2.5 border border-border/30">
                  <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>{event.output_gerado}</span>
                </div>
              </Section>
            )}

            {/* Notes */}
            {event.observacoes && (
              <Section title="Observações">
                <p className="text-sm text-foreground/80 bg-muted/30 rounded-lg p-2.5 border border-border/30">
                  {event.observacoes}
                </p>
              </Section>
            )}

            {/* Electronic validation / integrity */}
            <Section title="Validação Eletrônica">
              <div className="space-y-2 bg-muted/20 rounded-lg p-3 border border-border/30">
                <div className="flex items-center gap-2 text-xs">
                  <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-emerald-600 dark:text-emerald-400">Registro imutável — auditoria eletrônica</span>
                </div>
                {event.hash_registro && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase font-semibold">Hash SHA-256 do Registro</p>
                    <code className="text-xs font-mono bg-muted/50 p-1.5 rounded block break-all border border-border/20">
                      {event.hash_registro}
                    </code>
                  </div>
                )}
                {event.hash_anterior && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase font-semibold">Hash Anterior (Cadeia)</p>
                    <code className="text-xs font-mono bg-muted/50 p-1.5 rounded block break-all border border-border/20">
                      {event.hash_anterior}
                    </code>
                  </div>
                )}
                {!event.hash_registro && (
                  <p className="text-xs text-muted-foreground">
                    ID do Evento: <code className="font-mono">{event.id}</code>
                  </p>
                )}
                {event.ip_address && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Globe className="w-3 h-3" />
                    <span>IP de Origem: <code className="font-mono">{event.ip_address}</code></span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Timestamp: <code className="font-mono">{event.created_at}</code>
                </p>
              </div>
            </Section>

            {/* Data diffs */}
            {event.dados_anteriores && (
              <Section title="Dados Anteriores vs Novos">
                <div className="space-y-2">
                  <DiffView label="Antes" data={event.dados_anteriores} />
                  {event.dados_novos && <DiffView label="Depois" data={event.dados_novos} />}
                </div>
              </Section>
            )}

            {/* Snapshot */}
            {event.dados_snapshot && (
              <Section title="Snapshot de Dados">
                <DiffView label="Dados no momento da ação" data={event.dados_snapshot} />
              </Section>
            )}

            {/* Evidence */}
            {event.evidencia_ids && event.evidencia_ids.length > 0 && (
              <Section title={`Evidências (${event.evidencia_ids.length})`}>
                <div className="flex gap-1 flex-wrap">
                  {event.evidencia_ids.map((id) => (
                    <Badge key={id} variant="secondary" className="text-xs font-mono">{id.slice(0, 8)}</Badge>
                  ))}
                </div>
              </Section>
            )}

            {/* Entity info */}
            {event.entidade && (
              <Section title="Entidade">
                <div className="text-sm">
                  <span className="font-medium">{event.entidade}</span>
                  {event.entidade_id && (
                    <span className="text-muted-foreground ml-2 font-mono text-xs">#{event.entidade_id.slice(0, 8)}</span>
                  )}
                </div>
              </Section>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
